import React, { useState } from 'react';
import {
    DefaultButton,
    ITheme,
    MessageBar,
    MessageBarType,
    Pivot,
    PivotItem,
    Spinner,
    Stack,
    Text,
} from '@fluentui/react';
import {
    SreInflightItem,
    SreProgressStage,
} from '../../../../../AGSS/models/SreHealth';
import { SreProgressEntry } from './useSreProgress';
import { SreValidationFindings } from './SreValidationFindings';
import { SreOrchestrationFacts } from './SreOrchestrationFacts';
import { SreTransferFacts } from './SreTransferFacts';
import { SreFact } from './SreFact';
import { CopyButton } from './CopyButton';
import { findingEntries, findingsText } from './findingFormat';
import { formatDateTime, formatDuration } from './formatting';

interface Props {
    item?: SreInflightItem;
    identifier?: string;
    progress: SreProgressEntry | undefined;
    theme: ITheme;
    onRefresh: () => void;
    compact?: boolean;
}

const stageState = (stage: SreProgressStage): string =>
    !stage.completed
        ? 'In progress'
        : stage.successful
        ? 'Succeeded'
        : 'Failed';

const StageReport: React.FC<{
    stage: SreProgressStage;
    compact: boolean;
}> = ({ stage, compact }) => (
    <Stack tokens={{ childrenGap: 4 }}>
        {stage.statusMessage && (
            <details
                open={
                    compact &&
                    !stage.validationDetail &&
                    (!stage.completed || !stage.successful)
                }
            >
                <summary style={{ cursor: 'pointer', padding: '6px 0' }}>
                    {stage.stage} message
                </summary>
                <Stack horizontal horizontalAlign="end">
                    <CopyButton
                        label={`${stage.stage} message`}
                        value={stage.statusMessage}
                    />
                </Stack>
                <pre
                    role="region"
                    aria-label={`${stage.stage} message text`}
                    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- Keyboard focus enables native scrolling in this labelled region.
                    tabIndex={0}
                    style={{
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                        fontSize: 12,
                        lineHeight: '20px',
                        maxHeight: compact ? 160 : 320,
                        overflowY: 'auto',
                    }}
                >
                    {stage.statusMessage}
                </pre>
            </details>
        )}
    </Stack>
);

export const SreProgressDetail: React.FC<Props> = ({
    item,
    identifier,
    progress,
    theme,
    onRefresh,
    compact = false,
}) => {
    const [view, setView] = useState('table');
    if (!progress?.data) {
        if (progress?.error)
            return (
                <MessageBar
                    messageBarType={MessageBarType.error}
                    actions={<DefaultButton text="Retry" onClick={onRefresh} />}
                >
                    {progress.error}
                </MessageBar>
            );
        return <Spinner label="Loading stage progress..." />;
    }
    const data = progress.data;
    const uniqueIdentifier = identifier ?? data.healthDetail.uniqueIdentifier;
    const catalog = data.stageCatalog ?? [];
    const stageKey = (stage: SreProgressStage) =>
        stage.stageNumber === undefined
            ? stage.stage
            : String(stage.stageNumber);
    const stages = [...data.stages].sort(
        (left, right) =>
            (left.stageOrder ??
                catalog.find((entry) => entry.name === left.stage)?.order ??
                Number.MAX_SAFE_INTEGER) -
            (right.stageOrder ??
                catalog.find((entry) => entry.name === right.stage)?.order ??
                Number.MAX_SAFE_INTEGER)
    );
    const missing = catalog.filter(
        (entry) =>
            !stages.some(
                (stage) =>
                    stage.stageNumber === entry.number ||
                    stage.stage === entry.name
            )
    );
    const copyFindings = findingsText(uniqueIdentifier, stages);
    const entries = findingEntries(
        stages.flatMap((stage) => [
            ...(stage.validationDetail?.groups.flatMap((group) =>
                group.files.map((entry) => ({ entry }))
            ) ?? []),
            ...(stage.validationDetail?.hashingFailures.map((failure) => ({
                entry: failure.file,
            })) ?? []),
        ])
    );
    const partialPreview = stages.some(
        (stage) => stage.validationDetail?.truncated
    );
    const validationStages = stages.filter((stage) => stage.validationDetail);
    const cell: React.CSSProperties = {
        padding: '8px',
        textAlign: 'left',
        verticalAlign: 'top',
        borderBottom: `1px solid ${theme.palette.neutralLight}`,
    };

    return (
        <Stack
            tokens={{ childrenGap: 14 }}
            styles={{ root: { padding: compact ? 12 : 0, minWidth: 0 } }}
        >
            <Stack
                horizontal
                wrap
                verticalAlign="center"
                horizontalAlign="space-between"
                tokens={{ childrenGap: 8 }}
            >
                <Stack tokens={{ childrenGap: 4 }}>
                    <Text
                        styles={{
                            root: {
                                color: data.hasErrors
                                    ? theme.semanticColors.errorText
                                    : theme.palette.neutralPrimary,
                            },
                        }}
                    >
                        {data.hasErrors ? 'Has errors' : 'No errors'} · Total
                        elapsed {formatDuration(data.totalElapsedSeconds)}
                    </Text>
                    {!compact && (
                        <Text variant="small">
                            {data.healthDetail.stage} ·{' '}
                            {data.healthDetail.stageState} · Retries{' '}
                            {data.healthDetail.retryCount} · Orchestrations{' '}
                            {data.healthDetail.orchestrationCount}
                        </Text>
                    )}
                </Stack>
                <Stack horizontal tokens={{ childrenGap: 4 }}>
                    <DefaultButton
                        iconProps={{ iconName: 'Refresh' }}
                        text="Refresh"
                        disabled={progress.isLoading}
                        onClick={onRefresh}
                    />
                </Stack>
            </Stack>
            {progress.isLoading && <Spinner label="Refreshing progress..." />}
            {progress.error && (
                <MessageBar messageBarType={MessageBarType.error}>
                    {progress.error}
                </MessageBar>
            )}
            {!compact &&
                'artifactID' in data.healthDetail &&
                data.healthDetail.artifactID && (
                    <SreFact
                        label="Artifact ID"
                        value={data.healthDetail.artifactID}
                        copyable
                    />
                )}
            {(!compact || validationStages.length > 0) && (
                <section
                    aria-label="Validation findings"
                    style={{
                        minWidth: 0,
                        border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                        borderRadius: 4,
                    }}
                >
                    <Stack
                        horizontal
                        wrap
                        horizontalAlign="space-between"
                        verticalAlign="center"
                        tokens={{ childrenGap: 8 }}
                        styles={{ root: { padding: 12 } }}
                    >
                        <Stack tokens={{ childrenGap: 4 }}>
                            <Text
                                as="h3"
                                variant="mediumPlus"
                                styles={{
                                    root: { margin: 0, fontWeight: 600 },
                                }}
                            >
                                Validation findings
                            </Text>
                            <Text variant="small">
                                {entries.length.toLocaleString()} unique{' '}
                                {entries.length === 1 ? 'finding' : 'findings'}
                                {partialPreview ? ' in preview' : ''}
                            </Text>
                        </Stack>
                        {(entries.length > 0 || copyFindings) && (
                            <CopyButton
                                label="findings"
                                size="medium"
                                options={[
                                    {
                                        key: 'unique',
                                        text: `Unique files and hashes${
                                            partialPreview ? ' (preview)' : ''
                                        }`,
                                        value: entries.join('\n'),
                                    },
                                    ...(copyFindings
                                        ? [
                                              {
                                                  key: 'report',
                                                  text: `Detailed validation report${
                                                      partialPreview
                                                          ? ' (preview)'
                                                          : ''
                                                  }`,
                                                  value: copyFindings,
                                              },
                                          ]
                                        : []),
                                ]}
                            />
                        )}
                    </Stack>
                    {validationStages.length > 0 ? (
                        validationStages.map((stage) => (
                            <section
                                key={stageKey(stage)}
                                aria-label={`${stage.stage} findings`}
                                style={{
                                    padding: 12,
                                    borderTop: `1px solid ${theme.palette.neutralLight}`,
                                    minWidth: 0,
                                }}
                            >
                                <Text
                                    as="h4"
                                    styles={{
                                        root: {
                                            margin: '0 0 8px',
                                            fontWeight: 600,
                                        },
                                    }}
                                >
                                    {stage.stage}
                                </Text>
                                <SreValidationFindings
                                    detail={stage.validationDetail!}
                                    theme={theme}
                                    compact={compact}
                                />
                            </section>
                        ))
                    ) : (
                        <Text
                            as="div"
                            variant="small"
                            styles={{ root: { padding: '0 12px 12px' } }}
                        >
                            No validation findings recorded.
                        </Text>
                    )}
                </section>
            )}
            {!compact && <SreTransferFacts info={data.transferInfo} />}
            <section aria-label="Stage progress" style={{ minWidth: 0 }}>
                <Text
                    as="h3"
                    variant="mediumPlus"
                    styles={{ root: { margin: '0 0 8px', fontWeight: 600 } }}
                >
                    Stage progress
                </Text>
                <Pivot
                    selectedKey={view}
                    onLinkClick={(selected) =>
                        setView(selected?.props.itemKey ?? 'table')
                    }
                >
                    <PivotItem
                        itemKey="table"
                        headerText="Table"
                        itemIcon="ViewList"
                    />
                    <PivotItem
                        itemKey="timeline"
                        headerText="Timeline"
                        itemIcon="Timeline"
                    />
                </Pivot>
                {view === 'timeline' ? (
                    stages.map((stage) => (
                        <section
                            key={stageKey(stage)}
                            style={{
                                padding: '8px 0',
                                borderBottom: `1px solid ${theme.palette.neutralLight}`,
                            }}
                        >
                            <Stack
                                horizontal
                                wrap
                                verticalAlign="center"
                                tokens={{ childrenGap: 12 }}
                            >
                                <Text
                                    as="h4"
                                    styles={{
                                        root: { margin: 0, fontWeight: 600 },
                                    }}
                                >
                                    {stage.stageNumber === undefined
                                        ? ''
                                        : `${stage.stageNumber} · `}
                                    {stage.stage}
                                </Text>
                                <Text>{stageState(stage)}</Text>
                                {stage.stageOrder != null && (
                                    <Text variant="small">
                                        Step {stage.stageOrder} /{' '}
                                        {stage.stageCount ?? catalog.length}
                                    </Text>
                                )}
                                <Text variant="small">
                                    Duration{' '}
                                    {formatDuration(
                                        stage.executionDurationSeconds
                                    )}
                                </Text>
                                <Text variant="small">
                                    Pickup{' '}
                                    {formatDuration(
                                        stage.pickupDurationSeconds
                                    )}
                                </Text>
                                <Text variant="small">
                                    Retries {stage.retryCount} · Attempt{' '}
                                    {stage.orchestrationCount}
                                </Text>
                            </Stack>
                            <Text variant="small">
                                Written {formatDateTime(stage.writtenTime)} ·
                                Modified{' '}
                                {formatDateTime(stage.lastModifiedTime)}
                            </Text>
                            <StageReport stage={stage} compact={compact} />
                        </section>
                    ))
                ) : (
                    <div
                        role="region"
                        aria-label="Stage progress table"
                        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- Keyboard focus enables native scrolling in this labelled region.
                        tabIndex={0}
                        style={{ overflowX: 'auto' }}
                    >
                        <table
                            style={{
                                width: '100%',
                                minWidth: 940,
                                borderCollapse: 'collapse',
                                fontSize: 12,
                            }}
                            aria-label="Stage progress"
                        >
                            <thead>
                                <tr>
                                    {[
                                        'Stage',
                                        'State',
                                        'Written',
                                        'Last modified',
                                        'Duration',
                                        'Pickup',
                                        'Retries',
                                        'Attempt',
                                        'Report',
                                    ].map((heading) => (
                                        <th
                                            key={heading}
                                            scope="col"
                                            style={cell}
                                        >
                                            {heading}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {stages.map((stage) => (
                                    <tr key={stageKey(stage)}>
                                        <th scope="row" style={cell}>
                                            {stage.stageNumber === undefined
                                                ? ''
                                                : `${stage.stageNumber} · `}
                                            {stage.stage}
                                        </th>
                                        <td style={cell}>
                                            {stageState(stage)}
                                        </td>
                                        <td style={cell}>
                                            {formatDateTime(stage.writtenTime)}
                                        </td>
                                        <td style={cell}>
                                            {formatDateTime(
                                                stage.lastModifiedTime
                                            )}
                                        </td>
                                        <td style={cell}>
                                            {formatDuration(
                                                stage.executionDurationSeconds
                                            )}
                                        </td>
                                        <td style={cell}>
                                            {formatDuration(
                                                stage.pickupDurationSeconds
                                            )}
                                        </td>
                                        <td style={cell}>{stage.retryCount}</td>
                                        <td style={cell}>
                                            {stage.orchestrationCount}
                                        </td>
                                        <td style={cell}>
                                            <StageReport
                                                stage={stage}
                                                compact={compact}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {missing.length > 0 && (
                    <Text variant="small">
                        No report:{' '}
                        {missing.map((stage) => stage.name).join(', ')}
                    </Text>
                )}
                {data.healthDetail.latestStatusMessage && (
                    <details>
                        <summary
                            style={{ cursor: 'pointer', padding: '8px 0' }}
                        >
                            Latest status message
                        </summary>
                        <Stack
                            horizontal
                            horizontalAlign="space-between"
                            verticalAlign="center"
                        >
                            <CopyButton
                                label="latest status message"
                                value={data.healthDetail.latestStatusMessage}
                            />
                        </Stack>
                        <pre
                            role="region"
                            aria-label="Latest status message text"
                            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- Keyboard focus enables native scrolling in this labelled region.
                            tabIndex={0}
                            style={{
                                whiteSpace: 'pre-wrap',
                                overflowWrap: 'anywhere',
                                fontSize: 12,
                                lineHeight: '20px',
                                maxHeight: compact ? 160 : 320,
                                overflowY: 'auto',
                            }}
                        >
                            {data.healthDetail.latestStatusMessage}
                        </pre>
                    </details>
                )}
            </section>
            {!compact && (
                <details
                    style={{
                        borderTop: `1px solid ${theme.palette.neutralLight}`,
                    }}
                >
                    <summary style={{ cursor: 'pointer', padding: '12px 0' }}>
                        <Text
                            as="h3"
                            variant="mediumPlus"
                            styles={{
                                root: {
                                    display: 'inline',
                                    margin: 0,
                                    fontWeight: 600,
                                },
                            }}
                        >
                            Orchestration metadata
                        </Text>
                    </summary>
                    <SreOrchestrationFacts
                        detail={data.healthDetail}
                        item={item}
                        orchestrationType={data.orchestrationType}
                        icmId={data.icmId}
                    />
                </details>
            )}
        </Stack>
    );
};
